// @spec GHG-STR-024
'use client';

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ClipboardList, Users } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useGHGOrganizationId } from '@/features/ghg/api/use-ghg-organization-id';
import { CommutingCalculationPanel } from '@/features/ghg/components/CommutingCalculationPanel';
import { CommutingEmissionsBreakdown } from '@/features/ghg/components/CommutingEmissionsBreakdown';
import { useGHGPagePeriod } from '@/features/ghg/hooks/use-ghg-page-period';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/ghg/commuting')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.employeeCommuting'),
  }),
  component: CommutingPage,
});

/**
 * Scope 3 Category 7 (Employee Commuting) emissions page.
 *
 * Provides:
 * - List of completed commuting surveys ready for calculation
 * - Calculate emissions button for each survey
 * - Emissions breakdown by transport mode (car, bus, rail, bicycle, walking)
 * - Pie chart visualization
 * - Methodology documentation
 */
function CommutingPage() {
  const { t } = useTranslation('ghg');
  const { isLoaded: isOrgLoaded } = useOrganization();
  const organizationId = useGHGOrganizationId();
  const { periodId } = useGHGPagePeriod();

  if (!isOrgLoaded || !organizationId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <Skeleton className="h-9 w-48" />
          <Skeleton className="h-5 w-80" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Page Header */}
        <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">{t('commutingPage.heading')}</h1>
            <p className="mt-1 text-muted-foreground">
              {t('commutingPage.description')}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <Button asChild>
              <Link to="/ghg/commuting-surveys">
                <ClipboardList className="mr-2 h-4 w-4" />
                {t('commutingPage.manageSurveys')}
              </Link>
            </Button>
          </div>
        </header>

        <div className="space-y-6">
            {/* Calculation Panel and Breakdown */}
            <div className="grid gap-6 lg:grid-cols-2">
              <CommutingCalculationPanel
                organizationId={organizationId}
                reportingPeriodId={periodId}
              />
              <CommutingEmissionsBreakdown
                organizationId={organizationId}
                reportingPeriodId={periodId}
              />
            </div>

            {/* Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  {t('commutingPage.aboutHeading')}
                </CardTitle>
                <CardDescription>
                  {t('commutingPage.aboutDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-lg border p-4">
                    <h4 className="font-medium">{t('commutingPage.surveyBased')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t('commutingPage.surveyBasedDesc')}
                    </p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <h4 className="font-medium">{t('commutingPage.transportModes')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t('commutingPage.transportModesDesc')}
                    </p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <h4 className="font-medium">{t('commutingPage.carpoolAdjustment')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t('commutingPage.carpoolAdjustmentDesc')}
                    </p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <h4 className="font-medium">{t('commutingPage.ghgProtocol')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t('commutingPage.ghgProtocolDesc')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
        </div>
      </div>
    </div>
  );
}
